import { Entry, EntryJSON, MutableEntry } from '@databases/model/entry';
import { FieldType } from '@databases/model/field';
import { AllValueJSON, MutableValue } from '@databases/model/value';
import * as Y from 'yjs';

import { DefaultValueConverters } from './default-value-converters';
import { SynchronizedMap } from './helpers/synchronized-map';
import { DisposableElement, WrappedYType } from './helpers/types';
import { YValueType } from './values/types';
import { YDateValue, YDateValueWithoutCurrent } from './values/y-date-value';
import { YEntryLinkValue } from './values/y-entry-link-value';
import { YHyperlinkValue } from './values/y-hyperlink-value';
import { YImageValue } from './values/y-image-value';
import { YJiraIssueValue } from './values/y-jira-issue-value';
import { YNumberValue } from './values/y-number-value';
import { YPageLinkValue } from './values/y-page-link-value';
import { YSelectValue } from './values/y-select-value';
import { YTextValue } from './values/y-text-value';
import { YUserValue, YUserValueWithoutCurrent } from './values/y-user-value';
import { FieldMapping, YFieldType } from './y-field';

export const ValueProperties = {
  type: 't',
  value: 'v',
};

export const valueForType = (
  input: Y.Map<any>,
  type: any,
  isDefaultEntry: boolean,
  defaultValueConverters: DefaultValueConverters
): YValueType | undefined => {
  switch (type) {
    case YFieldType.TEXT:
      return new YTextValue(input);
    case YFieldType.DATE:
      return isDefaultEntry ? new YDateValue(input) : new YDateValueWithoutCurrent(input);
    case YFieldType.NUMBER:
      return new YNumberValue(input);
    case YFieldType.SELECT:
      return new YSelectValue(input);
    case YFieldType.IMAGE:
      return new YImageValue(input);
    case YFieldType.USER:
      return isDefaultEntry
        ? new YUserValue(input)
        : new YUserValueWithoutCurrent(input, defaultValueConverters.getCurrentUser);
    case YFieldType.PAGE_LINK:
      return new YPageLinkValue(input);
    case YFieldType.HYPERLINK:
      return new YHyperlinkValue(input);
    case YFieldType.ENTRY_LINK:
      return new YEntryLinkValue(input);
    case YFieldType.JIRA_ISSUE:
      return new YJiraIssueValue(input);
  }
};

export const valueFromJSON = (
  json: AllValueJSON,
  isDefaultEntry: boolean,
  defaultValueConverters: DefaultValueConverters
): Y.AbstractType<any> => {
  switch (json.type) {
    case FieldType.Text:
      return YTextValue.fromJSON(json).unwrap();
    case FieldType.Hyperlink:
      return YHyperlinkValue.fromJSON(json).unwrap();
    case FieldType.Date:
      return isDefaultEntry
        ? YDateValue.fromJSON(json).unwrap()
        : YDateValueWithoutCurrent.fromJSON(json).unwrap();
    case FieldType.Number:
      return YNumberValue.fromJSON(json).unwrap();
    case FieldType.Select:
      return YSelectValue.fromJSON(json).unwrap();
    case FieldType.User:
      return isDefaultEntry
        ? YUserValue.fromJSON(json).unwrap()
        : YUserValueWithoutCurrent.fromJSON(
            json,
            defaultValueConverters.getCurrentUser
          ).unwrap();
    case FieldType.Image:
      return YImageValue.fromJSON(json).unwrap();
    case FieldType.EntryLink:
      return YEntryLinkValue.fromJSON(json).unwrap();
    case FieldType.JiraIssue:
      return YJiraIssueValue.fromJSON(json).unwrap();
    case FieldType.PageLink:
      return YPageLinkValue.fromJSON(json).unwrap();
  }
};

export class YEntry implements Entry, MutableEntry, DisposableElement, WrappedYType {
  #values: SynchronizedMap<YValueType>;

  constructor(
    readonly data: Y.Map<unknown>,
    private isDefaultEntry: boolean,
    private defaultValueConverters: DefaultValueConverters
  ) {
    this.#values = new SynchronizedMap(data, (input) => {
      if (!(input instanceof Y.Map)) {
        return undefined;
      }

      const value = valueForType(
        input,
        input.get(ValueProperties.type),
        isDefaultEntry,
        defaultValueConverters
      );
      return value;
    });
  }

  observeDeep(cb: () => void) {
    this.data.observeDeep(cb);
  }

  unobserveDeep(cb: () => void) {
    this.data.unobserveDeep(cb);
  }

  setValue<T extends FieldType>(fieldId: string, type: T): MutableValue<T> {
    if (
      !this.#values.data[fieldId] ||
      (this.#values.data[fieldId] && type !== this.#values.data[fieldId].type)
    ) {
      const value = valueForType(
        new Y.Map([[ValueProperties.type, FieldMapping[type]]]),
        FieldMapping[type],
        this.isDefaultEntry,
        this.defaultValueConverters
      )!;

      this.#values.set(fieldId, value);

      return value as unknown as MutableValue<T>; // FIXME: can we improve this type?
    } else {
      return this.#values.data[fieldId] as unknown as MutableValue<T>; // FIXME: can we improve this type?
    }
  }

  getValue(fieldId: string) {
    return this.#values.data[fieldId];
  }

  removeValue(fieldId: string) {
    this.#values.delete(fieldId);
  }

  toJSON(): EntryJSON {
    return {
      values: Object.keys(this.#values.data).reduce((acc, key) => {
        acc[key] = this.#values.data[key].toJSON();
        return acc;
      }, {} as Record<string, AllValueJSON>),
    };
  }

  static fromJSON(
    json: EntryJSON,
    isDefaultEntry: boolean,
    defaultValueConverters: DefaultValueConverters
  ) {
    const entries: Array<[string, Y.AbstractType<unknown>]> = [];
    Object.keys(json.values).map((fieldId) => {
      entries.push([
        fieldId,
        valueFromJSON(json.values[fieldId], isDefaultEntry, defaultValueConverters),
      ]);
    });

    return new YEntry(new Y.Map(entries), isDefaultEntry, defaultValueConverters);
  }

  dispose(): void {
    this.#values.dispose();
  }

  unwrap(): Y.AbstractType<any> | undefined {
    return this.#values.unwrap();
  }
}
